import PropTypes from 'prop-types';
import { useCapacityFactor, useConversionsByMeasures, useIsSizedByNameplate } from '@/stores/anchorStore';
import { convert, convertFlowRate, getConversionOptions, getMeasures } from '@/utils/unitUtils';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Select from '../core/form/Select';
import usePrevious from '@/hooks/usePrevious';
import ErrorMessage from '../parameters/fields/ErrorMessage';
import { validatorRules } from '@/utils/validators';
import { ELECTRICITY_TYPES, FLOW_RATE_TYPES } from '@/consts';
import CostOverrideSummary from './CostOverrideSummary';
import { NumericFormatInput } from '../core/form/NumericFormatInput';
import SubmitButton from '../core/form/SubmitButton';
import CapacityInfo from './CapacityInfo';
import { CAPACITY_MODE } from './capacityMode';

const AnchorForm = ({
  defaultValues,
  onSubmit,
  onCancel,
  onToggleForm,
  ioName,
  ioType,
  typeName,
  costOverride,
  showOverrideForm,
}) => {
  const methods = useForm({ defaultValues, mode: 'onChange' });
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = methods;

  const [conversionStr, measure, flowRate, watchedValue] = useWatch({
    control,
    name: ['conversion', 'measure', 'flowRate', 'value'],
  });
  const prevConversionStr = usePrevious(conversionStr);
  const prevMeasure = usePrevious(measure);
  const prevFlowRate = usePrevious(flowRate);

  const capacityFactor = useCapacityFactor();
  const isSizedByNameplate = useIsSizedByNameplate();
  const conversionsByMeasures = useConversionsByMeasures(ioType, ioName);
  const conversions = conversionsByMeasures[measure];

  const options = getConversionOptions(conversions);
  const measures = getMeasures(conversionsByMeasures);
  const hasPowerMeasure = measures.some(m => m.value === 'power');
  // a plant pinned to a nameplate derives its capacity factor from output, so the
  // capacity factor shown here is not the one in effect and cannot convert a rated
  // capacity into an average output. The backend rejects a capacity basis in that mode.
  const showCapacityMode = capacityFactor >= 0 && hasPowerMeasure && !isSizedByNameplate;
  const [capacityMode, setCapacityMode] = useState(CAPACITY_MODE.POWER_OUTPUT);

  const handleCapacityModeChange = mode => {
    if (mode === capacityMode) return;
    const currentValue = parseFloat(getValues('value'));

    if (!isNaN(currentValue)) {
      const newValue =
        mode === CAPACITY_MODE.RATED_CAPACITY
          ? currentValue / (capacityFactor / 100)
          : currentValue * (capacityFactor / 100);
      setValue('value', parseFloat(newValue.toFixed(3)));
    }

    setCapacityMode(mode);
  };

  const checkKeyDown = e => {
    if (e.key === 'Enter') {
      handleSubmit(onSubmit)();
    }
  };

  const cancel = () => {
    reset();
    onCancel();
  };

  const submit = async data => {
    await onSubmit({
      ...data,
      ...(showCapacityMode && capacityMode === CAPACITY_MODE.RATED_CAPACITY && { anchorType: 'capacity' }),
    });
    reset();
  };

  // convert value based on the given conversion
  useEffect(() => {
    if (conversionStr && prevConversionStr) {
      const formValues = getValues();
      const from = JSON.parse(prevConversionStr);
      const to = JSON.parse(conversionStr);
      const value = parseFloat(formValues.value);
      const convertedByUnit = convert(from, to, value);
      const convertByFlowRate = convertFlowRate(prevFlowRate ?? 1, flowRate ?? 1, convertedByUnit);

      if (!isNaN(convertByFlowRate)) {
        setValue('value', convertByFlowRate);
      }
    }
  }, [conversionStr, prevConversionStr, flowRate, prevFlowRate, setValue, getValues]);

  useEffect(() => {
    if (measure && prevMeasure && measure !== prevMeasure) {
      const conversion = conversions[0];
      setValue('conversion', JSON.stringify(conversion));
    }
  }, [measure, prevMeasure, conversions, setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)} onKeyDown={checkKeyDown} className="mt-7">
        {showCapacityMode ? (
          <div className="mb-6 flex gap-2">
            <button
              type="button"
              className={`btn btn-sm ${capacityMode === CAPACITY_MODE.POWER_OUTPUT ? 'btn-active' : ''}`}
              onClick={() => handleCapacityModeChange(CAPACITY_MODE.POWER_OUTPUT)}
            >
              Power Output
            </button>
            <button
              type="button"
              className={`btn btn-sm ${capacityMode === CAPACITY_MODE.RATED_CAPACITY ? 'btn-active' : ''}`}
              onClick={() => handleCapacityModeChange(CAPACITY_MODE.RATED_CAPACITY)}
            >
              Rated Capacity
            </button>
          </div>
        ) : (
          <div className="mb-6 flex justify-between">
            {measures.map(measureTypes => (
              <input
                {...register('measure')}
                key={measureTypes.value}
                className="btn btn-sm"
                type="radio"
                aria-label={measureTypes.label}
                value={measureTypes.value}
              />
            ))}
          </div>
        )}
        <div className="join w-full">
          <div className="flex-1">
            <NumericFormatInput
              rules={{ required: true, valueAsNumber: true, validate: validatorRules.numeric() }}
              id="value"
              name="value"
              placeholder="Enter basis value"
              control={control}
            />
            {errors.value?.message && <ErrorMessage text={errors.value?.message.toString()} />}
          </div>
          <Select name="conversion" options={options} className="join-item select-sm border-l-0" />
          {!ELECTRICITY_TYPES.includes(typeName) && (
            <Select
              name="flowRate"
              className="select-sm ml-2"
              registerOptions={{ valueAsNumber: true }}
              options={FLOW_RATE_TYPES}
            />
          )}
        </div>
        {showCapacityMode && (
          <CapacityInfo
            capacityMode={capacityMode}
            value={parseFloat(watchedValue)}
            capacityFactor={capacityFactor}
            conversionStr={conversionStr}
          />
        )}
        <div className="modal-action justify-between items-center gap-6">
          {showOverrideForm && <CostOverrideSummary costOverride={costOverride} onToggleForm={onToggleForm} />}
          <div className="flex gap-2 flex-1 justify-end">
            <button onClick={cancel} type="button" className="btn btn-secondary btn-sm">
              Cancel
            </button>
            <SubmitButton label="Save" isPending={isSubmitting} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

AnchorForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  ioName: PropTypes.string,
  ioType: PropTypes.string,
  typeName: PropTypes.string,
  onToggleForm: PropTypes.func,
  costOverride: PropTypes.object,
  showOverrideForm: PropTypes.bool,
};

export default AnchorForm;
